import {
  Button,
  Divider,
  // Button,
  // Dropdown,
  // Menu,
  Image,
  Space,
  Typography,
} from 'antd';
import FAQItem from './FAQ';
import { useCallback, useMemo, useState } from 'react';
import { MercadoFAQ, MercadoItemFAQQuestion } from './services';
// import { MoreOutlined } from '@ant-design/icons';
import { useTranslate } from '@tolgee/react';
import { useGetRegionLabel } from 'hooks/business/useRegion';
import { PhrasesRootV2 } from 'services/api/phrase';
import { getMercadoProductLink } from 'views/Refund/components/Product';
import { openExternalURL } from 'utils/openExternalURL';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { useIsStoreAliasFirst } from 'hooks/business/store/useStoreName';

export interface ExpandState {
  id: string;
  questionIds: string[];
}

interface Props {
  preview?: boolean;
  faqInfo: MercadoFAQ;
  phraseData?: PhrasesRootV2;
  refreshList?: () => void;
  onChange: (itemId: string, item: MercadoFAQ) => void;
  expandState?: ExpandState;
  onExpandStateChange: (expandState: ExpandState) => void;
}

const Item = (props: Props) => {
  const { preview, faqInfo, phraseData, onChange, refreshList, expandState, onExpandStateChange } =
    props;
  const { externalItemId, itemName, imageUrl, region, shopName, shopAlias, itemSku } = faqInfo.item;
  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();
  const [visible, setVisible] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const isStoreAliasFirst = useIsStoreAliasFirst('MERCADO', region);

  const onFAQChange = useCallback(
    (faqId: string, faq?: MercadoItemFAQQuestion) => {
      const { item, question, ...rest } = faqInfo;
      onChange(faqInfo.id, {
        ...rest,
        item,
        question: faq
          ? question.map((x) => (x.id === faqId ? faq : x))
          : question.filter((x) => x.id !== faqId),
      });
    },
    [faqInfo, onChange],
  );

  const questionList = useMemo(() => {
    if (faqInfo?.question.length > 3) {
      const list = showAll ? faqInfo?.question : faqInfo?.question.slice(0, 3);
      return (
        <div>
          {list.map((x) => (
            <FAQItem
              preview={preview}
              questionItem={x}
              key={x.id}
              phraseData={phraseData}
              onChange={(faq) => onFAQChange(x.id, faq)}
              refreshList={refreshList}
              expand={
                expandState?.id === faqInfo.item.externalItemId &&
                expandState.questionIds.includes(x.id)
              }
              onExpandChange={(expand) =>
                onExpandStateChange({
                  id: faqInfo.item.externalItemId,
                  questionIds:
                    expandState?.id === faqInfo.item.externalItemId
                      ? expand
                        ? [...expandState.questionIds, x.id]
                        : expandState.questionIds.filter((_) => _ !== x.id)
                      : expand
                      ? [x.id]
                      : [],
                })
              }
            />
          ))}
          <div className="px-2">
            <Button
              icon={showAll ? <UpOutlined /> : <DownOutlined />}
              type="link"
              size="small"
              onClick={() => setShowAll(!showAll)}
            >
              {showAll ? t('phrase.收起') : t('展开全部')}
            </Button>
          </div>
        </div>
      );
    }

    return faqInfo?.question?.map((x) => (
      <FAQItem
        preview={preview}
        questionItem={x}
        key={x.id}
        phraseData={phraseData}
        onChange={(faq) => onFAQChange(x.id, faq)}
        refreshList={refreshList}
        expand={
          expandState?.id === faqInfo.item.externalItemId && expandState.questionIds.includes(x.id)
        }
        onExpandChange={(expand) =>
          onExpandStateChange({
            id: faqInfo.item.externalItemId,
            questionIds:
              expandState?.id === faqInfo.item.externalItemId
                ? expand
                  ? [...expandState.questionIds, x.id]
                  : expandState.questionIds.filter((_) => _ !== x.id)
                : expand
                ? [x.id]
                : [],
          })
        }
      />
    ));
  }, [
    faqInfo,
    showAll,
    preview,
    phraseData,
    refreshList,
    onFAQChange,
    setShowAll,
    t,
    expandState,
    onExpandStateChange,
  ]);

  return (
    <>
      <tr>
        <td colSpan={100} className="h-4" />
      </tr>
      <tr className="bg-[var(--fett-bg-panel)]">
        <td className="pr-24 pl-4 py-4">
          <div className="flex gap-3">
            <div className="flex-none w-14 h-14 overflow-hidden">
              <Image
                src={imageUrl?.[0]}
                onClick={() => setVisible(true)}
                className=""
                width={56}
                preview={{ visible: false }}
                height={56}
              />

              <div className="hidden">
                <Image.PreviewGroup
                  preview={{ visible, onVisibleChange: (vis) => setVisible(vis) }}
                >
                  {imageUrl?.map((url) => (
                    <Image src={url} key={url} className="" />
                  ))}
                </Image.PreviewGroup>
              </div>
            </div>
            <div className="flex-1">
              <Typography.Paragraph
                className="cursor-pointer hover:text-primary-hover"
                ellipsis={{ rows: 2, tooltip: itemName }}
                onClick={() => {
                  const url = getMercadoProductLink(externalItemId, region);
                  url && openExternalURL(url);
                }}
              >
                {itemName}
              </Typography.Paragraph>
              <Space className="text-xs text-[var(--fett-text-tertiary)]">
                <div>ID: {externalItemId}</div>
                <Divider type="vertical" />
                <div className="line-clamp-2" title={itemSku}>
                  {t('商品货号')}：{itemSku || '-'}
                </div>
                <Divider type="vertical" />
                <div>
                  {getRegionLabel(region)}-{isStoreAliasFirst ? shopAlias || shopName : shopName}
                </div>
              </Space>
            </div>
          </div>
        </td>
        {/* <td className="pr-24 pl-4 py-4">
          <div>U$S 12.99 x 5u.</div>
          <div className=" text-[var(--fett-text-tertiary)]">COP $51.137</div>
        </td>
        <td className="pr-24 pl-4 py-4">
          <div>免息分期付款</div>
          <div>免邮</div>
        </td> */}
        {/* <td className="pl-4 py-4 text-right pr-8">
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item>
                  <Button>{t('前往卖家中心')}</Button>
                </Menu.Item>
              </Menu>
            }
          >
            <MoreOutlined />
          </Dropdown>
        </td> */}
      </tr>
      <tr className="border-0 border-t bg-[var(--fett-bg-panel)] border-[var(--fett-border-default)] border-solid relative w-full rounded-bl-lg rounded-br-lg ">
        <td className="w-full pb-2" colSpan={100}>
          {questionList}
        </td>
      </tr>
    </>
  );
};

export default Item;
